import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';

import { DownloadExcelButton } from 'reports/Common/DownloadExcelButton';

import CN from './Hours.module.scss';

const allTooltips = (t) => [
  {
    id: 1,
    text: t('businessReports.hours.tooltips.timeOff.tooltip'),
    label: t('businessReports.hours.tooltips.timeOff.label'),
  },
  {
    id: 2,
    text: t('businessReports.hours.tooltips.workingDays.tooltip'),
    label: t('businessReports.hours.tooltips.workingDays.label'),
  },
  {
    id: 3,
    text: t('businessReports.hours.tooltips.workingHours.tooltip'),
    label: t('businessReports.hours.tooltips.workingHours.label'),
  },
  {
    id: 4,
    text: t('businessReports.hours.tooltips.occupancy.tooltip'),
    label: t('businessReports.hours.tooltips.occupancy.label'),
  },
  {
    id: 5,
    text: t('businessReports.hours.tooltips.busyHours.tooltip'),
    label: t('businessReports.hours.tooltips.busyHours.label'),
  },
  {
    id: 6,
    text: t('businessReports.hours.tooltips.clientTime.tooltip'),
    label: t('businessReports.hours.tooltips.clientTime.label'),
  },
  {
    id: 7,
    text: t('businessReports.hours.tooltips.busyTime.tooltip'),
    label: t('businessReports.hours.tooltips.busyTime.label'),
  },
  {
    id: 8,
    text: t('businessReports.hours.tooltips.freeTime.tooltip'),
    label: t('businessReports.hours.tooltips.freeTime.label'),
  },
];

const Hours = ({ salonHoursReport, startDate, endDate, stylistIDInCalendarOrder }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const stylistGroupHours = salonHoursReport.lastRequestResponse.stylist_group || {};

  const fromMinutesToHoursStringTwoLineSplit = (num) => {
    if (Number.isNaN(num)) return { h: '', m: '' };

    const hours = Math.floor(num / 60);
    const minutes = Math.floor(num % 60);

    return {
      h: t('businessReports.hours.workingHours', { count: hours }),
      m: t('businessReports.hours.workingMinutes', { count: minutes }),
    };
  };

  const tooltipContents = allTooltips(t);

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>{t('businessReports.hours.staff')}</th>
      {tooltipContents.map(({ text, label }, index) => {
        const isLast = index === tooltipContents.length - 1;
        return (
          <th key={createNodeKey({ index })}>
            <div className={CN.tableHeaderText}>
              <div className={CN.labelSpace}>{label}</div>
              <Tooltip content={text} variant={isLast ? 'quaternaryLeftToBottom' : 'quaternaryBottom'} width="max">
                <div className={CN.iconTooltip}>
                  <Icon variant="info" width="sm" />
                </div>
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const tableRowsData = stylistIDInCalendarOrder
    .filter((stylist) => stylistGroupHours[stylist.id])
    .map((stylist) => {
      const stylistData = stylistGroupHours[stylist.id];

      const stylistName = truncateString({ value: stylistData.name, max: 24, suffix: '...' });
      const timeOff = fromMinutesToHoursStringTwoLineSplit(stylistData.time_off_mins || 0);
      const actualMins = fromMinutesToHoursStringTwoLineSplit(stylistData.actual_mins || 0);
      const busyMins = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins || 0);
      const busyOnServices = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins_on_services || 0);
      const busyOff = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins_off || 0);
      const freeMins = fromMinutesToHoursStringTwoLineSplit(stylistData.free_mins || 0);
      const actualDays = stylistData.actual_days || 0;
      const occupancyPct = `${stylistData.occupancy_pct || 0}%`;

      return {
        stylistId: stylist.id,
        stylistName,
        timeOff,
        actualMins,
        actualDays,
        occupancyPct,
        busyMins,
        busyOnServices,
        busyOff,
        freeMins,
      };
    });

  const tableRows = asArray(
    tableRowsData.map((row) => {
      const { stylistId, stylistName, timeOff, actualMins, actualDays, occupancyPct, busyMins, busyOnServices, busyOff, freeMins } = row;
      return (
        <tr key={stylistId}>
          <td className={CN.firstCell}>{stylistName}</td>
          <td>
            <div>{timeOff.h}</div>
            <div>{timeOff.m}</div>
          </td>
          <td>{actualDays}</td>
          <td>
            <div>{actualMins.h}</div>
            <div>{actualMins.m}</div>
          </td>
          <td>{occupancyPct}</td>
          <td>
            <div>{busyMins.h}</div>
            <div>{busyMins.m}</div>
          </td>
          <td>
            <div>{busyOnServices.h}</div>
            <div>{busyOnServices.m}</div>
          </td>
          <td>
            <div>{busyOff.h}</div>
            <div>{busyOff.m}</div>
          </td>
          <td>
            <div>{freeMins.h}</div>
            <div>{freeMins.m}</div>
          </td>
        </tr>
      );
    }),
  );

  const createCsv = useCallback(() => {
    const headers = [t('businessReports.hours.staff'), ...tooltipContents.map((tooltip) => tooltip.label)];

    const formatHM = (obj) => `${obj.h}\n${obj.m}`;

    const csvData = asArray(tableRowsData).map((item) => {
      const values = [
        item.stylistName,
        formatHM(item.timeOff),
        item.actualDays,
        formatHM(item.actualMins),
        item.occupancyPct,
        formatHM(item.busyMins),
        formatHM(item.busyOnServices),
        formatHM(item.busyOff),
        formatHM(item.freeMins),
      ];

      return Object.fromEntries(headers.map((header, i) => [header, values[i]]));
    });

    return csvData;
  }, [tableRowsData, tooltipContents, t]);

  const hasTableData = fromAnyToBool(tableRowsData.length);

  return (
    <div
      data-bem="Hours"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>{t('businessReports.hours.staffWorkingHours')}</div>
        <div className={CN.buttonsContainer}>
          <DownloadExcelButton getCsvData={createCsv} fileName={t('businessReports.hours.csvFileName')} disabled={!hasTableData} />
        </div>
      </div>
      <div className={CN.timeContainer}>
        <div>
          {t('businessReports.hours.from')} <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>{t('businessReports.hours.to')}</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="Hours">
          <thead>{tableHeader}</thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonHoursReport: state?.reports?.salonHoursReport,
  stylistIDInCalendarOrder: state?.reports?.stylistIDInCalendarOrder,
});

const HoursHoc = connect(mapStateToProps)(Hours);

export default HoursHoc;
